import React, { useEffect, useMemo, useState } from 'react';
import {
  GamepadIcon,
  BookOpenIcon,
  RefreshCwIcon,
  SkullIcon,
  ClockIcon,
  MessageSquareIcon,
  SettingsIcon,
  LoaderIcon,
  AlertCircleIcon,
  BookOpenCheckIcon,
  TrashIcon,
  Sparkles,
  ChevronRight,
  History as HistoryIcon,
  Ghost,
  PlusIcon,
  UserIcon
} from 'lucide-react';
import clsx from 'clsx';
import { DungeonBotConfigPage } from '../components/DungeonBotConfigPage.js';
import { BotPageLayout } from '../components/shared/BotPageLayout.js';
import { formatDuration, formatTimestamp } from '../lib/discord-utils.js';
import {
  useDungeonSessions,
  useDungeonSessionDetails,
  useDungeonSessionEvents,
  useDungeonMetrics,
  useDungeonCharacters,
  useDungeonServerSettings,
  useDungeonSessionMutations,
} from '../hooks/useDungeon.js';
import type { DungeonParticipant, DungeonCharacterRecord } from '../lib/api/core-services.js';
import type { DungeonSessionDeletePreview, DungeonSessionBulkDeleteResult } from '../lib/api/domains/dungeon.js';
import { Button, Card, Alert, Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Badge, Progress } from '@randompedestrian07/ui';

const AVATAR_COLORS = ['bg-emerald-500', 'bg-indigo-500', 'bg-purple-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500'];

const ParticipantAvatar: React.FC<{ userId: string; characterName?: string | null; displayName?: string | null; avatarUrl?: string | null; isActive?: boolean | null; isAlive?: boolean | null; index: number; size?: 'sm' | 'md' }> = ({ userId, characterName, displayName, avatarUrl, isActive, isAlive, index, size = 'md' }) => {
  const fallback = (characterName || displayName || userId).slice(0, 2).toUpperCase();
  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const base = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <div className="relative">
      {avatarUrl ? (
        <img src={avatarUrl} alt={characterName || displayName || userId} className={clsx(base, "rounded-full object-cover")} />
      ) : (
        <div className={clsx(base, color, "rounded-full flex items-center justify-center font-bold text-white")}>{fallback}</div>
      )}
      {isAlive === false && (
        <div className="absolute -top-1 -right-1 bg-red rounded-full p-0.5">
          <SkullIcon size={10} className="text-white" />
        </div>
      )}
      {isActive && isAlive !== false && (
        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green rounded-full border-2 border-panel" />
      )}
    </div>
  );
};

const ParticipantRow: React.FC<{ participant: DungeonParticipant; index: number }> = ({ participant, index }) => (
  <div className="flex items-center gap-2 p-2 rounded-lg bg-panel hover:bg-panel-strong transition-colors">
    <ParticipantAvatar
      userId={participant.userId}
      characterName={participant.characterName}
      displayName={participant.displayName}
      avatarUrl={participant.avatarUrl}
      isActive={participant.isActive}
      isAlive={participant.isAlive}
      index={index}
      size="sm"
    />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-text truncate">{participant.characterName || participant.displayName || participant.userId}</div>
      {participant.characterName && <div className="text-xs text-muted truncate">{participant.displayName}</div>}
    </div>
    {participant.isAlive === false && <Badge variant="destructive" className="text-[10px]">Dead</Badge>}
  </div>
);

export type DungeonBotSection = 'sessions' | 'characters' | 'configuration';

interface DungeonBotPageProps {
  initialSection?: DungeonBotSection;
}

export const DungeonBotPage: React.FC<DungeonBotPageProps> = ({ initialSection = 'sessions' }) => {
  const [activeSection, setActiveSection] = useState<DungeonBotSection>(initialSection);

  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);

  const tabs = [
    { id: 'sessions', label: 'Sessions', icon: <GamepadIcon size={16} /> },
    { id: 'characters', label: 'Characters', icon: <BookOpenIcon size={16} /> },
    { id: 'configuration', label: 'Configuration', icon: <SettingsIcon size={16} /> },
  ];

  return (
    <BotPageLayout
      icon={<GamepadIcon className="text-green" size={32} />}
      iconColorClass="bg-green/10 shadow-green/10"
      title="Dungeon Bot"
      subtitle="Lethal Adventures"
      tabs={tabs}
      activeTab={activeSection}
      onTabChange={(id) => setActiveSection(id as DungeonBotSection)}
    >
      {activeSection === 'sessions' && <GameSessionsSection />}
      {activeSection === 'characters' && <CharacterManagementSection />}
      {activeSection === 'configuration' && <DungeonBotConfigPage />}
    </BotPageLayout>
  );
};

const GameSessionsSection: React.FC = () => {
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const { data: sessionsData, isLoading, isError, refetch, isFetching } = useDungeonSessions({
    limit: pageSize,
    offset: pageIndex * pageSize,
  });
  const { data: metrics } = useDungeonMetrics();
  const sessions = useMemo(() => sessionsData?.sessions ?? [], [sessionsData]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [deletePreview, setDeletePreview] = useState<DungeonSessionDeletePreview | null>(null);
  const [deleteResult, setDeleteResult] = useState<DungeonSessionBulkDeleteResult | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { previewBulkDeleteSessions, bulkDeleteSessions } = useDungeonSessionMutations();

  const totalSessions = sessionsData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalSessions / pageSize));
  const currentPage = Math.min(pageIndex + 1, totalPages);

  useEffect(() => {
    if (sessions.length === 0) {
      setSelectedSessionId(null);
    } else if (!selectedSessionId || !sessions.find((s) => s.id === selectedSessionId)) {
      setSelectedSessionId(sessions[0].id);
    }
  }, [sessions, selectedSessionId]);

  useEffect(() => {
    setSelectedSessionIds((prev) => prev.filter((id) => sessions.some((session) => session.id === id)));
  }, [sessions]);

  useEffect(() => {
    setPageIndex((prev) => Math.min(prev, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  const selectedSummary = useMemo(() => sessions.find((session) => session.id === selectedSessionId) ?? null, [sessions, selectedSessionId]);
  const { data: sessionDetails, isLoading: detailsLoading } = useDungeonSessionDetails(selectedSessionId ?? undefined);
  const { data: sessionEvents, isLoading: eventsLoading } = useDungeonSessionEvents(selectedSessionId ?? undefined, { limit: 6 });
  const { data: serverSettings } = useDungeonServerSettings(selectedSummary?.guildId ?? undefined);

  const selectedCount = selectedSessionIds.length;
  const allSelected = sessions.length > 0 && selectedCount === sessions.length;
  const deleteBusy = previewBulkDeleteSessions.isPending || bulkDeleteSessions.isPending;
  const mismatchCount = deletePreview?.summary.mismatchCount ?? 0;
  const missingThreadCount = deletePreview?.summary.missingThread ?? 0;
  const discordMissingCount = deletePreview?.summary.discordMissing ?? 0;
  const safeToDeleteCount = missingThreadCount + discordMissingCount;

  const toggleSessionSelection = (sessionId: string, checked: boolean) => {
    setSelectedSessionIds((prev) => {
      if (checked) {
        return prev.includes(sessionId) ? prev : [...prev, sessionId];
      }
      return prev.filter((id) => id !== sessionId);
    });
  };

  const handleSelectAll = () => {
    setSelectedSessionIds(sessions.map((session) => session.id));
  };

  const handleClearSelection = () => {
    setSelectedSessionIds([]);
  };

  const handleOpenDeleteDialog = async () => {
    if (selectedCount === 0) return;
    setDeleteError(null);
    setDeleteResult(null);
    try {
      const preview = await previewBulkDeleteSessions.mutateAsync(selectedSessionIds);
      setDeletePreview(preview);
      setDeleteDialogOpen(true);
    } catch (error) {
      setDeleteError((error as Error).message || 'Unable to preview deletion');
    }
  };

  const handleDelete = async (deleteDiscord: boolean) => {
    if (selectedCount === 0) return;
    setDeleteError(null);
    try {
      const safeToDeleteStatuses = ['missing_thread', 'discord_missing'];
      const deletableIds = deletePreview?.results
        .filter((item) => safeToDeleteStatuses.includes(item.status))
        .map((item) => item.sessionId) ?? [];
      if (!deleteDiscord && deletableIds.length === 0) {
        setDeleteError('No sessions eligible for safe deletion.');
        return;
      }
      const result = await bulkDeleteSessions.mutateAsync({
        sessionIds: deleteDiscord ? selectedSessionIds : deletableIds,
        deleteDiscord,
        forceUiDelete: !deleteDiscord,
      });
      setDeleteResult(result);
      setDeleteDialogOpen(false);
      setSelectedSessionIds([]);
    } catch (error) {
      setDeleteError((error as Error).message || 'Unable to delete sessions');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-medium">Active Game Sessions</h2>
          {metrics && (
            <p className="text-xs text-muted mt-1">
              {metrics.games_active_total} active · {metrics.games_completed_total} completed · {metrics.turns_processed_total} turns processed
            </p>
          )}
          <div className="mt-2 flex items-center space-x-3 text-xs text-muted">
            <span>{selectedCount} selected</span>
            <button
              className="text-text/80 hover:text-text disabled:text-muted"
              onClick={handleSelectAll}
              disabled={sessions.length === 0 || allSelected}
            >
              Select all on page
            </button>
            <button
              className="text-text/80 hover:text-text disabled:text-muted"
              onClick={handleClearSelection}
              disabled={selectedCount === 0}
            >
              Clear
            </button>
            <span className="text-muted">Page {currentPage} of {totalPages}</span>
          </div>
        </div>
        <div className="flex space-x-3">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => {
              const nextSize = Number(value);
              setPageSize(nextSize);
              setPageIndex(0);
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[25, 50, 100].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  Show {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="secondary"
            onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
            disabled={pageIndex === 0}
          >
            Prev
          </Button>
          <Button
            variant="secondary"
            onClick={() => setPageIndex((prev) => Math.min(totalPages - 1, prev + 1))}
            disabled={pageIndex >= totalPages - 1}
          >
            Next
          </Button>
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCwIcon size={14} className={clsx('mr-1', { 'animate-spin': isFetching })} /> Refresh
          </Button>
          <Button
            variant="destructive"
            onClick={handleOpenDeleteDialog}
            disabled={selectedCount === 0 || deleteBusy}
          >
            <TrashIcon size={14} className="mr-1" /> Delete Selected
          </Button>
          <Button
            disabled
            title={serverSettings?.dungeonSettings?.allowedChannelId
              ? `Start from Discord in #${serverSettings.dungeonSettings.allowedChannelId}`
              : 'Start from Discord (UI coming soon)'}
          >
            <PlusIcon size={14} className="mr-1" /> New Game Session
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center text-muted mb-6">
          <LoaderIcon className="mr-2 animate-spin" size={16} /> Loading sessions…
        </div>
      )}

      {isError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon className="mr-2" size={16} /> Unable to load sessions right now.
        </Alert>
      )}

      {deleteError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon className="mr-2" size={16} /> {deleteError}
        </Alert>
      )}

      {deleteResult && (
        <Alert className="mb-6 bg-green/10 border-green/40 text-green">
          <BookOpenCheckIcon className="mr-2" size={16} />
          Deleted {deleteResult.summary.deleted + deleteResult.summary.deletedUiOnly} sessions, skipped {deleteResult.summary.skipped}, missing {deleteResult.summary.missing}.
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {sessions.map((session) => (
          <Card
            key={session.id}
            className={clsx(
              'overflow-hidden cursor-pointer transition-all duration-300 group hover:shadow-2xl hover:shadow-green/5 hover:-translate-y-1',
              selectedSessionId === session.id
                ? 'border-green ring-1 ring-green/20 bg-green/5'
                : 'border-stroke hover:border-green/40 bg-panel-strong/20'
            )}
            onClick={() => setSelectedSessionId(session.id)}
          >
            <div className="relative h-44 bg-panel-strong overflow-hidden flex items-center justify-center border-b border-stroke">
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" />
              <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 items-end">
                <Badge
                  variant="outline"
                  className={clsx(
                    'text-[10px] font-bold uppercase tracking-widest border px-2 py-0.5',
                    session.gameState === 'active' && 'bg-green/10 text-green border-green/30',
                    session.gameState === 'lobby' && 'bg-accent/10 text-accent border-accent/30',
                    session.gameState === 'paused' && 'bg-warning/10 text-warning border-warning/30',
                    session.gameState === 'ended' && 'bg-muted/10 text-muted border-stroke'
                  )}
                >
                  {session.gameState}
                </Badge>
                <div className="bg-black/40 backdrop-blur-md px-2 py-1 rounded-lg border border-white/5 flex items-center text-[10px] font-bold text-white/90">
                  <ClockIcon size={10} className="mr-1 text-green" />
                  {formatDuration(session.metrics.durationMs)}
                </div>
              </div>
              <div className="p-6 relative z-10 w-full">
                <div className="flex items-start gap-4 mb-2">
                  <div className="p-2.5 bg-green/10 rounded-xl border border-green/20">
                    <GamepadIcon size={24} className="text-green" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight line-clamp-2 pr-12">
                      {session.name}
                    </h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mt-1">
                      ID: {session.id.slice(0, 8)} • Channel {session.channelId}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex -space-x-3">
                  {session.participantsPreview.slice(0, 5).map((participant, index) => (
                    <div key={index} className="ring-2 ring-bg-solid rounded-full overflow-hidden">
                      <ParticipantAvatar
                        userId={participant.userId}
                        characterName={participant.characterName}
                        displayName={participant.displayName}
                        avatarUrl={participant.avatarUrl || undefined}
                        isActive={participant.isActive}
                        isAlive={participant.isAlive}
                        index={index}
                        size="sm"
                      />
                    </div>
                  ))}
                  {session.participantsPreview.length > 5 && (
                    <div className="h-8 w-8 rounded-full bg-panel-strong border-2 border-bg-solid flex items-center justify-center text-[10px] font-bold text-muted">
                      +{session.participantsPreview.length - 5}
                    </div>
                  )}
                  {session.participantsPreview.length === 0 && (
                    <span className="text-xs italic text-muted/60">No participants yet</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded-lg border-stroke bg-panel text-green accent-green cursor-pointer transition-all hover:border-green"
                    checked={selectedSessionIds.includes(session.id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleSessionSelection(session.id, event.target.checked)}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled
                  title={serverSettings?.dungeonSettings?.allowedChannelId
                    ? `Join from Discord in #${serverSettings.dungeonSettings.allowedChannelId}`
                    : 'Join from Discord (UI coming soon)'}
                >
                  <UserIcon size={12} className="mr-1" /> Join
                </Button>
                {session.threadId ? (
                  <a
                    href={`https://discord.com/channels/${session.guildId ?? '@me'}/${session.threadId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-green/80 hover:bg-green text-bg-solid py-1.5 px-3 rounded-sm text-xs flex items-center"
                    title="Open thread in Discord"
                  >
                    <MessageSquareIcon size={12} className="mr-1" /> Open Thread
                  </a>
                ) : (
                  <Button variant="secondary" size="sm" disabled>
                    <MessageSquareIcon size={12} className="mr-1" /> No Thread
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogTitle>Delete {deletePreview?.summary.requested} sessions</DialogTitle>
          {mismatchCount > 0 ? (
            <>
              <DialogDescription>
                {mismatchCount} sessions have issues. {safeToDeleteCount > 0 && `${safeToDeleteCount} can be safely deleted (no Discord cleanup needed).`}
              </DialogDescription>
              <div className="text-xs text-muted space-y-1 mb-4">
                {missingThreadCount > 0 && <div>No threadId in database: {missingThreadCount}</div>}
                {deletePreview.summary.discordMissing > 0 && <div>Thread already deleted from Discord: {deletePreview.summary.discordMissing}</div>}
                {deletePreview.summary.missingSessions > 0 && <div>Session not found: {deletePreview.summary.missingSessions}</div>}
                {deletePreview.summary.alreadyDeleted > 0 && <div>Already deleted: {deletePreview.summary.alreadyDeleted}</div>}
                {deletePreview.summary.discordError > 0 && <div>Discord errors (unknown state): {deletePreview.summary.discordError}</div>}
                {deletePreview.summary.discordUnavailable > 0 && <div>Discord unavailable: {deletePreview.summary.discordUnavailable}</div>}
              </div>
            </>
          ) : (
            <DialogDescription>
              This will delete Discord threads and mark sessions deleted. Make sure you want to nuke them.
            </DialogDescription>
          )}
          <DialogFooter className="flex flex-col space-y-3">
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleteBusy}
              >
                Cancel
              </Button>
              {mismatchCount > 0 ? (
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(false)}
                  disabled={deleteBusy || safeToDeleteCount === 0}
                >
                  Delete Safe Sessions ({safeToDeleteCount})
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(true)}
                  disabled={deleteBusy}
                >
                  Delete and Nuke Threads
                </Button>
              )}
            </div>
            {mismatchCount > 0 && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => {
                  bulkDeleteSessions.mutateAsync({
                    sessionIds: selectedSessionIds,
                    deleteDiscord: false,
                    forceUiDelete: true,
                  }).then((result) => {
                    setDeleteResult(result);
                    setDeleteDialogOpen(false);
                    setSelectedSessionIds([]);
                  }).catch((error) => {
                    setDeleteError((error as Error).message || 'Unable to delete sessions');
                  });
                }}
                disabled={deleteBusy}
              >
                Force Delete All (Skip Discord Thread Cleanup)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedSummary && (
        <Card className="overflow-hidden border-stroke bg-panel/30 backdrop-blur-md shadow-2xl rounded-3xl group">
          <div className="bg-panel-strong/60 px-8 py-5 border-b border-stroke flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-green/10 rounded-xl">
                <GamepadIcon size={20} className="text-green" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg text-text tracking-tight">{selectedSummary.name}</span>
                  <Badge variant="secondary" className="bg-panel-soft text-[10px] py-0 px-2 h-5 font-bold border-stroke">{selectedSummary.id}</Badge>
                </div>
                {selectedSummary.threadId && (
                  <a
                    href={`https://discord.com/channels/${selectedSummary.guildId ?? '@me'}/${selectedSummary.threadId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] font-bold text-accent hover:text-accent-hover flex items-center gap-1 mt-0.5"
                  >
                    DISCORD THREAD <MessageSquareIcon size={10} />
                  </a>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-col items-end">
                <span className="text-[9px] font-bold text-muted uppercase tracking-widest opacity-60">Duration</span>
                <div className="flex items-center text-sm font-semibold text-text/80">
                  <ClockIcon size={14} className="mr-1.5 text-green" />
                  {formatDuration(selectedSummary.metrics.durationMs)}
                </div>
              </div>
              <div className="h-8 w-px bg-stroke hidden md:block" />
              <div className="flex flex-col items-end max-w-[400px]">
                <span className="text-[9px] font-bold text-muted uppercase tracking-widest opacity-60">Current Goal</span>
                <div className="text-sm font-semibold text-text/80 line-clamp-1 italic">
                  "{selectedSummary.promptSnippet || 'No prompt provided'}"
                </div>
              </div>
            </div>
          </div>
          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-panel-strong/40 rounded-3xl p-6 border border-stroke shadow-inner min-h-[200px] relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-5">
                    <Sparkles size={120} />
                  </div>
                  {serverSettings?.dungeonSettings?.allowedChannelId && (
                    <div className="text-[10px] font-bold text-accent bg-accent/10 border border-accent/20 px-4 py-2 rounded-xl mb-6 inline-flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                      Commands restricted to{' '}
                      <a
                        href={`https://discord.com/channels/${selectedSummary?.guildId ?? '@me'}/${serverSettings.dungeonSettings.allowedChannelId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-accent/40 hover:text-accent-hover transition-colors"
                      >
                        #{serverSettings.dungeonSettings.allowedChannelId}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted/60">Current Narrative</h4>
                    <span className="text-[10px] font-semibold text-muted/40">LAST UPDATE: {formatTimestamp(selectedSummary.updatedAt)}</span>
                  </div>
                  {detailsLoading ? (
                    <div className="flex items-center justify-center py-12 text-muted font-medium text-sm gap-3">
                      <LoaderIcon className="animate-spin text-accent" size={20} /> Loading...
                    </div>
                  ) : (
                    <div className="relative">
                      <p className="text-lg text-text/90 leading-relaxed font-serif italic">
                        {sessionDetails?.context.lastNarrative || selectedSummary.promptSnippet || 'The abyss is silent…'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="bg-panel-strong/40 rounded-3xl p-6 border border-stroke shadow-inner">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted/60">Fateful Votes</h4>
                    <Badge variant="secondary" className="bg-warning/10 text-warning border-warning/20">{selectedSummary.activeVotes.length} ACTIVE</Badge>
                  </div>
                  {selectedSummary.activeVotes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSummary.activeVotes.map((vote) => (
                        <div key={vote.id} className="bg-panel-strong/60 p-5 rounded-2xl border border-stroke shadow-lg group/vote transition-all hover:border-warning/40">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <span className="text-[10px] font-bold text-warning uppercase tracking-widest block mb-1">{vote.type} VOTE</span>
                              <h5 className="font-bold text-sm text-text">Consensus Required</h5>
                            </div>
                            <div className="h-10 w-10 rounded-full border border-stroke flex items-center justify-center bg-panel font-bold text-sm">
                              {Math.round((vote.currentVotes / vote.requiredVotes) * 100)}%
                            </div>
                          </div>
                          <Progress value={(vote.currentVotes / vote.requiredVotes) * 100} className="h-1.5 bg-stroke mb-3" />
                          <div className="flex justify-between text-[10px] font-bold uppercase tracking-tighter text-muted">
                            <span>{vote.currentVotes} VOICES</span>
                            <span>{vote.requiredVotes} NEEDED</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-12 flex flex-col items-center justify-center text-muted opacity-40 border-2 border-dashed border-stroke rounded-2xl">
                      <p className="text-xs font-bold uppercase tracking-widest mt-2">No active votes</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-panel-strong/40 rounded-3xl p-6 border border-stroke shadow-inner">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted/60">Adventurers</h4>
                  <Badge
                    variant="secondary"
                    className={clsx(
                      'text-[10px] font-bold border px-2 py-0.5 rounded-lg',
                      selectedSummary?.gameState === 'active' && 'bg-green/10 text-green border-green/20',
                      selectedSummary?.gameState === 'lobby' && 'bg-accent/10 text-accent border-accent/20',
                      selectedSummary?.gameState === 'paused' && 'bg-warning/10 text-warning border-warning/20',
                      selectedSummary?.gameState === 'ended' && 'bg-muted/10 text-muted border-stroke'
                    )}
                  >
                    {selectedSummary?.gameState.toUpperCase()}
                  </Badge>
                </div>
                {detailsLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted font-medium text-xs gap-3">
                    <LoaderIcon className="animate-spin text-accent" size={16} /> Loading...
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(sessionDetails?.participants ?? []).map((participant, index) => (
                      <div key={participant.id ?? `${participant.userId}-${index}`} className="group/player transition-all">
                        <ParticipantRow participant={participant} index={index} />
                        {participant.characterDescription && (
                          <div className="ml-[52px] mt-2 text-[11px] text-text/60 leading-relaxed font-medium bg-panel-soft/30 p-3 rounded-xl border border-stroke/20 border-dashed italic">
                            {participant.characterDescription}
                          </div>
                        )}
                      </div>
                    ))}
                    {(sessionDetails?.participants ?? []).length === 0 && (
                      <div className="py-8 text-center text-muted text-[10px] font-bold uppercase tracking-widest opacity-30">No participants yet</div>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-panel-strong/40 rounded-3xl p-6 border border-stroke shadow-inner">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted/60">Session Chronology</h4>
                  <HistoryIcon size={14} className="text-muted/40" />
                </div>
                {eventsLoading ? (
                  <div className="flex items-center justify-center py-8 text-muted font-medium text-xs gap-3">
                    <LoaderIcon className="animate-spin text-accent" size={16} /> Loading...
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-stroke">
                    {(sessionEvents?.events ?? []).map((event, index) => {
                      const timestampValue = event.ts;
                      const eventDate =
                        typeof timestampValue === 'string' || typeof timestampValue === 'number'
                          ? new Date(timestampValue)
                          : timestampValue instanceof Date
                            ? timestampValue
                            : undefined;
                      const eventText =
                        (event.narration && typeof event.narration === 'object' ? event.narration.text : undefined) ||
                        (typeof event.actionText === 'string' ? event.actionText : undefined) ||
                        'Unknown phenomenon recorded';
                      return (
                        <div key={event.id ?? `${index}-${String(timestampValue ?? index)}`} className="relative pl-6 before:absolute before:left-0 before:top-2 before:bottom-0 before:w-px before:bg-stroke last:before:bg-transparent">
                          <div className="absolute left-[-4px] top-[6px] w-2 h-2 rounded-full bg-accent/40 ring-4 ring-bg-solid" />
                          <div className="text-[9px] font-black tracking-widest text-muted/30 uppercase mb-1">
                            {formatTimestamp(eventDate?.toISOString())}
                          </div>
                          <div className="text-xs text-text/80 leading-relaxed font-medium italic">
                            "{eventText}"
                          </div>
                        </div>
                      );
                    })}
                    {(sessionEvents?.events ?? []).length === 0 && (
                      <div className="py-8 text-center text-muted text-[10px] font-bold uppercase tracking-widest opacity-30">
                        No events recorded
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

const CharacterManagementSection: React.FC = () => {
  const { data: characterData, isLoading, isError, refetch, isFetching } = useDungeonCharacters({ limit: 50 });
  const characters = useMemo(() => characterData?.characters ?? [], [characterData]);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);

  useEffect(() => {
    if (characters.length === 0) {
      setSelectedCharacterId(null);
    } else if (!selectedCharacterId || !characters.find((c) => c.id === selectedCharacterId)) {
      setSelectedCharacterId(characters[0].id);
    }
  }, [characters, selectedCharacterId]);

  const selectedCharacter = useMemo(() => characters.find((character) => character.id === selectedCharacterId) ?? null, [characters, selectedCharacterId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <div className="lg:col-span-1">
        <Card className="overflow-hidden border-stroke bg-panel/30 backdrop-blur-md rounded-[2.5rem] shadow-2xl relative group">
          <div className="p-6 border-b border-stroke flex justify-between items-center bg-panel-strong/40">
            <h3 className="text-[11px] font-black uppercase tracking-widest text-muted/60 flex items-center">
              <BookOpenIcon size={14} className="mr-2 text-green" /> Roster
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 rounded-full hover:bg-green/10 hover:text-green"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCwIcon size={12} className={clsx({ 'animate-spin': isFetching })} />
            </Button>
          </div>
          {isLoading && (
            <div className="p-12 flex flex-col items-center justify-center text-muted gap-3">
              <LoaderIcon className="animate-spin text-accent" size={24} />
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Loading...</span>
            </div>
          )}
          {isError && (
            <div className="m-6 p-4 rounded-2xl bg-red/5 border border-red/20 text-red text-center">
              <p className="text-[10px] font-black uppercase tracking-widest mb-1">Roster Failed</p>
              <p className="text-xs font-medium">Unable to load entities</p>
            </div>
          )}
          <div className="max-h-[700px] overflow-y-auto scrollbar-thin scrollbar-thumb-stroke px-2 py-4 space-y-2">
            {characters.map((character, index) => (
              <div
                key={character.id}
                className={clsx(
                  'p-4 rounded-2xl flex items-center cursor-pointer transition-all duration-300 group/char',
                  selectedCharacterId === character.id
                    ? 'bg-accent/10 border border-accent/20 shadow-lg shadow-accent/5'
                    : 'hover:bg-panel-soft/50 border border-transparent hover:border-stroke'
                )}
                onClick={() => setSelectedCharacterId(character.id)}
              >
                <ParticipantAvatar
                  userId={character.userId}
                  characterName={character.displayName}
                  displayName={character.displayName}
                  avatarUrl={character.avatarUrl || undefined}
                  index={index}
                  size="sm"
                />
                <div className="ml-4 overflow-hidden">
                  <div className={clsx(
                    "font-bold text-sm tracking-tight truncate transition-colors",
                    selectedCharacterId === character.id ? "text-accent" : "text-text/80 group-hover/char:text-text"
                  )}>
                    {character.displayName || 'Unnamed Character'}
                  </div>
                  <div className="text-[9px] font-black uppercase tracking-widest text-muted/40 mt-1 truncate">
                    {character.guildId ? `GUILD: ${character.guildId.slice(0, 8)}` : 'GLOBAL'}
                  </div>
                </div>
              </div>
            ))}
            {characters.length === 0 && !isLoading && (
              <div className="py-24 text-center text-muted opacity-30 flex flex-col items-center gap-4">
                <Ghost size={32} />
                <p className="text-[10px] font-black uppercase tracking-widest">No characters</p>
              </div>
            )}
          </div>
        </Card>
      </div>
      <div className="lg:col-span-3">
        {selectedCharacter ? (
          <CharacterDetail character={selectedCharacter} />
        ) : (
          <div className="h-full min-h-[500px] flex flex-col items-center justify-center border-2 border-dashed border-stroke rounded-[3rem] text-center p-12 opacity-30">
            <BookOpenCheckIcon size={64} className="mb-6" />
            <h3 className="text-2xl font-black uppercase tracking-widest text-text/80 mb-2">Select a Character</h3>
            <p className="text-sm font-medium italic mb-8 max-w-sm">Choose a character from the roster to view their details and history.</p>
            <Button disabled className="h-12 rounded-2xl px-10 text-[11px] font-black uppercase tracking-widest bg-panel-strong border border-stroke">
              Create New Character
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const CharacterDetail: React.FC<{ character: DungeonCharacterRecord }> = ({ character }) => {
  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
      <Card className="overflow-hidden border-stroke bg-panel/30 backdrop-blur-md rounded-[3rem] shadow-2xl relative group">
        <div className="absolute top-0 right-0 p-12 opacity-[0.03] group-hover:opacity-10 transition-opacity">
          <BookOpenIcon size={240} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-0 relative z-10">
          <div className="md:col-span-4 p-10 bg-panel-strong/40 flex flex-col items-center border-r border-stroke shadow-inner">
            <div className="relative mb-8">
              <div className="absolute -inset-4 bg-accent/20 rounded-full blur-2xl opacity-20 animate-pulse" />
              <ParticipantAvatar
                userId={character.userId}
                characterName={character.displayName}
                displayName={character.displayName}
                avatarUrl={character.avatarUrl || undefined}
                index={0}
              />
            </div>

            <h2 className="text-3xl font-black text-text tracking-tight text-center leading-tight mb-2">
              {character.displayName || 'Unnamed Character'}
            </h2>
            <div className="flex items-center gap-2 mb-8">
              <Badge variant="secondary" className="bg-panel-soft text-[10px] font-bold border-stroke uppercase tracking-widest">
                ID: {character.id.slice(0, 12)}...
              </Badge>
            </div>

            <div className="w-full space-y-3 mt-auto">
              <div className="bg-panel/40 border border-stroke/40 rounded-2xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[9px] font-black uppercase tracking-widest text-muted/40">Affiliation</span>
                </div>
                <div className="text-xs font-bold text-text truncate">
                  {character.guildId ? `Server ${character.guildId}` : 'Global Character'}
                </div>
              </div>

              <div className="bg-panel/40 border border-stroke/40 rounded-2xl p-4 shadow-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[9px] font-black uppercase tracking-widest text-muted/40">Created</span>
                </div>
                <div className="text-xs font-bold text-text truncate">
                  {formatTimestamp(character.createdAt)}
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-8 p-10 bg-panel/20 backdrop-blur-sm space-y-10">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-[11px] font-black uppercase tracking-widest text-muted/60">Description</h4>
                <div className="flex gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <div className="w-1.5 h-1.5 rounded-full bg-accent/40" />
                  <div className="w-1.5 h-1.5 rounded-full bg-accent/10" />
                </div>
              </div>
              <div className="bg-panel-strong/40 rounded-3xl p-8 border border-stroke shadow-inner relative overflow-hidden group/text">
                <div className="absolute top-0 right-0 p-4 opacity-[0.02] group-hover/text:opacity-5 transition-opacity">
                  <Sparkles size={120} />
                </div>
                <p className="text-lg text-text/90 leading-relaxed font-serif italic relative z-10">
                  "{character.description || 'No description provided.'}"
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-panel-strong/40 p-6 rounded-3xl border border-stroke shadow-inner">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted/40 block mb-3">Status</span>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green animate-pulse" />
                  <span className="text-xs font-bold text-green uppercase tracking-widest">Linked</span>
                </div>
              </div>
              <div className="bg-panel-strong/40 p-6 rounded-3xl border border-stroke shadow-inner">
                <span className="text-[9px] font-black uppercase tracking-widest text-muted/40 block mb-3">Health</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-accent uppercase tracking-widest">Nominal</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
